import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { CreditCard, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';

import { loginSchema } from '../../utils/validationSchemas';
import { authService } from '../../services/authService';
import useAuthStore from '../../store/authStore';
import Mascot from '../../components/common/Mascot';

const Login = () => {
  const navigate = useNavigate();
  const { login: setAuth, user, isAuthenticated } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      dni: '',
      password: '',
    },
  });

  // Navegar cuando el usuario esté autenticado
  useEffect(() => {
    console.log('useEffect - Estado:', { isAuthenticated, user: user?.rol, pendingNavigation });
    if (isAuthenticated && user && pendingNavigation) {
      console.log('Usuario autenticado, navegando a:', pendingNavigation);
      navigate(pendingNavigation, { replace: true });
      setPendingNavigation(null);
    }
  }, [isAuthenticated, user, pendingNavigation, navigate]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const response = await authService.login(data);

      // El API devuelve: { token, id, username, dni, email, rol }
      const { token, ...userData } = response;
      
      toast.success(`¡Bienvenido!`);

      // Determinar ruta según rol
      let targetRoute;
      switch (userData.rol) {
        case 'ADMIN':
        case 'SISTEMAS':
          targetRoute = '/admin/dashboard';
          break;
        case 'SUPERVISOR':
          targetRoute = '/supervisor/dashboard';
          break;
        case 'JEFE_SELECCION':
          console.log('USUARIO JEFE_SELECCION - Preparando navegación a jefe-seleccion/dashboard');
          targetRoute = '/jefe-seleccion/dashboard';
          break;
        case 'ANALISTA':
          targetRoute = '/analista/procesos';
          break;
        case 'RECLUTADOR':
          targetRoute = '/reclutador/procesos';
          break;
        case 'POSTULANTE':
          targetRoute = '/postulante/dashboard';
          break;
        default:
          targetRoute = '/';
      }

      // Guardar la ruta pendiente y luego actualizar el auth
      setPendingNavigation(targetRoute);
      console.log('Llamando setAuth con:', userData);
      setAuth(userData, token);
      console.log('setAuth llamado, esperando que useEffect se active...');
    } catch (error) {
      console.error('Error en login:', error);
      toast.error(
        error.response?.data?.message ||
        'Error al iniciar sesión. Verifica tus credenciales.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-neutral-50 flex">
      {/* Panel Izquierdo - Branding */}
      <motion.div
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="hidden lg:flex lg:w-[48%] bg-gradient-to-br from-secondary-600 via-secondary-500 to-secondary-600 relative overflow-hidden"
      >
        {/* Patrón de fondo abstracto */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full opacity-10">
            <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-32 right-20 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
            <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-white/50 rounded-full blur-2xl"></div>
          </div>

          {/* Grid pattern */}
          <div className="absolute inset-0 opacity-5" style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          {/* Logo corporativo */}
          <div className="space-y-6">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="inline-block"
            >
            </motion.div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.5 }}
            >
              <h2 className="text-4xl font-bold mb-3 leading-tight">
                Bienvenido de nuevo
              </h2>
              <p className="text-secondary-100 text-lg">
                Accede a tu cuenta y continúa gestionando tus procesos de manera eficiente
              </p>
            </motion.div>
          </div>

          {/* Mascota */}
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            className="flex justify-center"
          >
            <Mascot variant="login" />
          </motion.div>
        </div>
      </motion.div>

      {/* Panel Derecho - Formulario */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-8 relative">
        {/* Logo en esquina superior derecha */}
        <div className="hidden lg:block absolute top-8 right-8">
          
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="w-full max-w-md"
        >
          

          {/* Título del formulario */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-neutral-900 mb-2">
              Iniciar Sesión
            </h1>
            <p className="text-neutral-600">
              Ingresa tus credenciales para acceder
            </p>
          </div>

          {/* Formulario */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* DNI Input */}
            <div className="space-y-2">
              <label className="text-sm font-semibold text-neutral-700 block">
                Documento de Identidad
              </label>
              <div className="relative group">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 group-focus-within:text-secondary transition-colors">
                  <CreditCard className="w-5 h-5" />
                </div>
                <input
                  type="text"
                  placeholder="12345678"
                  className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl focus:outline-none transition-all ${errors.dni
                      ? 'border-red-300 focus:border-red-500 bg-red-50/50'
                      : 'border-neutral-200 focus:border-secondary focus:ring-4 focus:ring-secondary/10 bg-white'
                    }`}
                  {...register('dni')}
                />
              </div>
              {errors.dni && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-sm text-red-600 flex items-center gap-1.5"
                >
                  <span className="w-1.5 h-1.5 bg-red-600 rounded-full"></span>
                  {errors.dni.message}
                </motion.p>
              )}
            </div>

            {/* Password Input */}
            <div className="space-y-2">
              <label className="text-sm font-semibold text-neutral-700 block">
                Contraseña
              </label>
              <div className="relative group">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 group-focus-within:text-secondary transition-colors">
                  <Lock className="w-5 h-5" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  className={`w-full pl-12 pr-12 py-3 border-2 rounded-xl focus:outline-none transition-all ${errors.password
                      ? 'border-red-300 focus:border-red-500 bg-red-50/50'
                      : 'border-neutral-200 focus:border-secondary focus:ring-4 focus:ring-secondary/10 bg-white'
                    }`}
                  {...register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-sm text-red-600 flex items-center gap-1.5"
                >
                  <span className="w-1.5 h-1.5 bg-red-600 rounded-full"></span>
                  {errors.password.message}
                </motion.p>
              )}
            </div>

            

            {/* Botón Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-secondary via-secondary-600 to-secondary-700 hover:from-secondary-700 hover:to-secondary-800 text-white font-semibold py-3.5 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:shadow-secondary/30 disabled:opacity-50 disabled:cursor-not-allowed group mt-2"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                <>
                  <span>Ingresar</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>
          
          
          {/* Botón de limpieza de caché */}
          <button
            onClick={() => {
              localStorage.clear();
              sessionStorage.clear();
              toast.success('Caché limpiada correctamente');
              window.location.reload();
            }}
            className="mt-2 text-xs text-neutral-400 hover:text-neutral-600 transition-colors underline"
          >
            Presiona si tienes problemas
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;